import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "../api/axios";
import "./styles/OrderSuccess.css";

import { useCart } from "../context/CartContext";
import { getImageUrl } from "../utility/imageHelper";
import { slugify } from "../utility/slugify";

import SEO from "../seo/SEO";
import config from "../config/config";

import { useAnalytics } from "../context/AnalyticsProvider";

const OrderSuccess = () => {
  const { orderUuid } = useParams();
  const [trackingNumber, setTrackingNumber] = useState("");
  const [carrier, setCarrier] = useState(""); // ✅ Store carrier from backend
  const [orderFound, setOrderFound] = useState(true); // ✅ Track if the order exists
  const [orderStatus, setOrderStatus] = useState("");
  const [totalPrice, setTotalPrice] = useState(0);
  const analytics = useAnalytics();
  const purchaseFiredRef = useRef(false);

  const [products, setProducts] = useState([]);


  const { clearCart } = useCart();

  const [loading, setLoading] = useState(true);


  const [orderCreatedAt, setOrderCreatedAt] = useState(null);
  const [showEmailMessage, setShowEmailMessage] = useState(false);
  
  useEffect(() => {
  const fetchOrder = async () => {
    try {
      const res = await axios.get(`/api/orders/verify?order_uuid=${orderUuid}`);
      if (res.data.order) {
        if (res.data.order.tracking_number) {
          setTrackingNumber(res.data.order.tracking_number);
        }
        if (res.data.order.carrier) {
          setCarrier(res.data.order.carrier.toUpperCase()); // Ensure consistency
        }

        if (res.data.order.status) {
          setOrderStatus(res.data.order.status.toLowerCase());
        }

        if (res.data.order.created_at) {
          setOrderCreatedAt(new Date(res.data.order.created_at));
        }

        if (res.data.order.total_price) {
          setTotalPrice(parseFloat(res.data.order.total_price));
        }

        if (res.data.order.products) {
          setProducts(res.data.order.products);
        }
      } else {
        setOrderFound(false);
      }
    } catch (error) {
      console.error("🔥 Failed to fetch order tracking info:", error);
      setOrderFound(false);
    } finally {
      setLoading(false);
    }
  };

  fetchOrder();
}, [orderUuid]); // ✅ Removed clearCart from dependencies


  // ✅ Runs only when `orderStatus` updates to "paid"
  useEffect(() => {
    if (orderStatus === "paid") {
      clearCart();
    }
  }, [orderStatus, clearCart]);
  
  // ✅ Check if order was placed within the last 5 minutes
  useEffect(() => {
    if (orderCreatedAt) {
      const now = new Date();
      const diffInMinutes = (now - orderCreatedAt) / (1000 * 60);
      setShowEmailMessage(diffInMinutes <= 5);
    }
  }, [orderCreatedAt]);
  

    useEffect(() => {
    // Don't run until loading is done
    if (loading) return;

    // Order not found or not paid = don't fire purchase
    if (!orderFound || orderStatus !== "paid") return;

    // Already fired? Never fire twice
    if (purchaseFiredRef.current) return;

    // Must have items
    if (!products || products.length === 0) return;

    const items = products.map(p => ({
      item_id: p.product_id,
      item_name: p.name,
      price: Number(p.price) / p.quantity, // because backend stored price * quantity
      quantity: p.quantity,
      category: p.category_name,  // ← ADD THIS
      stock_remaining: p.stock ? p.stock - p.quantity : null, // ← ADD THIS if available
    }));

    console.log(items)

    analytics.track("purchase", {
      transaction_id: orderUuid,
      currency: "USD",
      value: products.reduce((sum, p) => sum + Number(p.price), 0), // price column already includes qty multiplier
      customer_type: "new", // or compute this later if you want
      items,
    });

    purchaseFiredRef.current = true; // prevent double firing
  }, [loading, orderFound, orderStatus, products, orderUuid, analytics]);


  

  if (loading) {
    return (
      <div className="order-success">
        <h2>Checking Your Order...</h2>
        <div className="spinner"></div>
      </div>
    );
  }

  if (!orderFound) {
    return (
      <div className="order-success">
        <h2>❌ Order Not Found</h2>
        <p>We couldn't find an order with the ID: {orderUuid}</p>
        <Link to="/shop/all-products" className="continue-shopping-btn">
          Back to Shop
        </Link>
      </div>
    );
  }




  return (
    <>
        <SEO
            robots="noindex, nofollow"
            title="Order Confirmation - Withrow Knives"
            description={`Thank you for your order! Your order ID: ${orderUuid}. Track your order and find more details here.`}
            keywords={[
                "order confirmation",
                "purchase success",
                "order tracking",
                "Withrow Knives order",
            ]}
            url={`${config.metadata.siteUrl}/order-success/${orderUuid}`}
            jsonLd={{
                "@context": "https://schema.org",
                "@type": "Order",
                "name": "Order Confirmation - Withrow Knives",
                "url": `${config.metadata.siteUrl}/order-success/${orderUuid}`,
                "description": `Your order has been successfully placed. Order ID: ${orderUuid}. Track your package and view details.`,
                "orderNumber": orderUuid,
                "orderStatus": trackingNumber ? "OrderShipped" : "OrderProcessing",
                "potentialAction": {
                    "@type": "TrackAction",
                    "target": trackingNumber
                        ? `https://tools.usps.com/go/TrackConfirmAction?qtc_tLabels1=${trackingNumber}`
                        : `${config.metadata.siteUrl}/user/my-orders`,
                    "name": trackingNumber ? "Track Order" : "View Order Status"
                },
                "breadcrumb": {
                    "@type": "BreadcrumbList",
                    "itemListElement": [
                        {
                            "@type": "ListItem",
                            "position": 1,
                            "name": "Home",
                            "item": config.metadata.siteUrl
                        },
                        {
                            "@type": "ListItem",
                            "position": 2,
                            "name": "My Orders",
                            "item": `${config.metadata.siteUrl}/user/my-orders`
                        },
                        {
                            "@type": "ListItem",
                            "position": 3,
                            "name": "Order Confirmation",
                            "item": `${config.metadata.siteUrl}/order-success/${orderUuid}`
                        }
                    ]
                },
                "mainEntityOfPage": {
                    "@type": "WebPage",
                    "@id": `${config.metadata.siteUrl}/order-success/${orderUuid}`
                }
            }}
        />


      <div className="order-success">
        <h2 aria-live="polite">🎉 Thank You for Your Order!</h2>
        {/* ✅ Dynamic Order Confirmation Message */}
        <p>
          {orderStatus === "shipped" && "Your order has been shipped and is on its way!"}
          {orderStatus === "cancelled" && "Your order has been cancelled. If this was a mistake, please contact support."}
          {orderStatus === "complete" && "Your order has been successfully completed!"}
          {["pending", "paid"].includes(orderStatus) && "Your order is being processed and will be shipped soon."}
        </p>

        
        <p>
          <strong>Order ID:</strong> 
          <span aria-label="Order ID"> {orderUuid}</span>
        </p>





        {showEmailMessage && (
          <p>You will receive a confirmation email with your order details shortly.</p>
        )}

        {trackingNumber && carrier && (
          <p>
            <strong>Tracking:</strong>{" "}
            <a
              href={
                carrier === "USPS"
                  ? `https://tools.usps.com/go/TrackConfirmAction?qtc_tLabels1=${trackingNumber}`
                  : carrier === "UPS"
                  ? `https://www.ups.com/track?tracknum=${trackingNumber}`
                  : carrier === "FEDEX"
                  ? `https://www.fedex.com/fedextrack/?trknbr=${trackingNumber}`
                  : "/"
              }
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Track your package with ${carrier}, tracking number ${trackingNumber}`}
            >
              Track Your Package ({carrier})
            </a>
          </p>
        )}


        <hr/>
        <h3>Order Details</h3>
        <p style={{margin: 0}}>
          <strong>Total Price:</strong> 
          <span aria-label="Total Price"> ${totalPrice.toFixed(2)}</span>
        </p>
        <p>
          <strong>Date placed:</strong>
          <span aria-label="Order Date"> {orderCreatedAt.toLocaleString(undefined, { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
        </p>
        {products.length > 0 && (
          <div className="order-products">
            
            <ul>
              {products.map((product) => (
                <Link className="orders-view-product-page" to={`/shop-knives/knife/${product.product_id}/${slugify(product.name)}`}>
                  <li key={product.product_id} className="order-product">
                    <img
                      src={getImageUrl(product.image_url)}
                      alt={product.name}
                      className="order-product-img"
                    />
                    <div className="order-product-details">
                      <span className="product-name">{product.name} (#{product.product_id})</span>
                      <span className="product-qty">Qty: {product.quantity}</span>
                      <span className="product-price">${product.price.toFixed(2)}</span>
                    </div>
                  </li>
                </Link>
              ))}
            </ul>
          </div>
        )}


        <Link to="/shop-knives/all-knives" className="continue-shopping-btn" aria-label="Continue shopping for more products">
          Continue Shopping
        </Link>
      </div>

    </>
  );
};

export default OrderSuccess;
